import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Clock, Calendar, GitCompare, Copy, Check } from 'lucide-react'
import { apiService } from '@/services/apiService'
import { useToast } from '@/hooks/use-toast'
import { useIncognito } from '@/hooks/use-incognito'

interface PoolHistoryViewProps {
  poolID: string
}

export default function PoolHistoryView({ poolID }: PoolHistoryViewProps) {
  const [selectedDateTime, setSelectedDateTime] = useState('')
  const [historyData, setHistoryData] = useState<{
    systemIDs: string[]
    timestamp: string
  } | null>(null)
  const [loading, setLoading] = useState(false)

  const [fromDateTime, setFromDateTime] = useState('')
  const [toDateTime, setToDateTime] = useState('')
  const [comparisonData, setComparisonData] = useState<{
    from: { systemIDs: string[]; timestamp: string }
    to: { systemIDs: string[]; timestamp: string }
  } | null>(null)
  const [comparing, setComparing] = useState(false)

  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const { toast } = useToast()
  const incognito = useIncognito()

  const handleCopyIds = async (key: string, systemIDs: string[]) => {
    try {
      await navigator.clipboard.writeText(systemIDs.join('\n'))
      setCopiedKey(key)
      toast({
        title: 'Copied',
        description: `${systemIDs.length} system ID${systemIDs.length !== 1 ? 's' : ''} copied to clipboard`,
      })
      setTimeout(() => setCopiedKey(null), 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  const renderCopyButton = (key: string, systemIDs: string[]) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 shrink-0"
      onClick={() => handleCopyIds(key, systemIDs)}
      disabled={systemIDs.length === 0}
      title="Copy system IDs"
    >
      {copiedKey === key ? (
        <Check className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </Button>
  )

  const formatTimestamp = (timestamp: string) =>
    new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })

  const handleFetchHistory = async () => {
    if (!selectedDateTime) {
      toast({
        title: 'Date Required',
        description: 'Please select a date and time',
        variant: 'destructive',
      })
      return
    }

    setLoading(true)
    try {
      // Convert local datetime to ISO string (UTC)
      const isoTimestamp = new Date(selectedDateTime).toISOString()
      const response = await apiService.pools.getHistory(poolID, isoTimestamp)

      if (response.success && response.data) {
        setHistoryData(response.data)
        toast({
          title: 'History Loaded',
          description: `Found ${response.data.systemIDs.length} system(s) at that time`,
        })
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to fetch pool history',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch pool history',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCompare = async () => {
    if (!fromDateTime || !toDateTime) {
      toast({
        title: 'Dates Required',
        description: 'Please select a date and time for both State 1 and State 2',
        variant: 'destructive',
      })
      return
    }

    if (new Date(toDateTime) <= new Date(fromDateTime)) {
      toast({
        title: 'Invalid Range',
        description: 'State 2 must be after State 1',
        variant: 'destructive',
      })
      return
    }

    setComparing(true)
    try {
      // Convert local datetimes to ISO strings (UTC)
      const fromIso = new Date(fromDateTime).toISOString()
      const toIso = new Date(toDateTime).toISOString()

      const [fromResponse, toResponse] = await Promise.all([
        apiService.pools.getHistory(poolID, fromIso),
        apiService.pools.getHistory(poolID, toIso),
      ])

      if (
        fromResponse.success &&
        fromResponse.data &&
        toResponse.success &&
        toResponse.data
      ) {
        setComparisonData({ from: fromResponse.data, to: toResponse.data })
        toast({
          title: 'Comparison Loaded',
          description: `${fromResponse.data.systemIDs.length} system(s) → ${toResponse.data.systemIDs.length} system(s)`,
        })
      } else {
        toast({
          title: 'Error',
          description:
            fromResponse.error || toResponse.error || 'Failed to fetch pool history',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch pool history',
        variant: 'destructive',
      })
    } finally {
      setComparing(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Pool History
        </CardTitle>
        <CardDescription>
          View which systems were members of the pool at a specific point in time
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Snapshot sub-panel */}
        <div className="border rounded-lg p-4 space-y-4">
          <div>
            <h3 className="text-base font-semibold">Snapshot</h3>
            <p className="text-sm text-muted-foreground">
              Select a date and time and view the systems that were members of the pool at that
              moment.
            </p>
          </div>

          {/* Date/Time Picker */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="datetime" className="text-sm font-medium block mb-2">
                Select Date & Time
              </label>
              <input
                id="datetime"
                type="datetime-local"
                value={selectedDateTime}
                onChange={(e) => setSelectedDateTime(e.target.value)}
                disabled={loading}
                max={new Date().toISOString().slice(0, 16)} // Can't pick future dates
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleFetchHistory} disabled={loading || !selectedDateTime}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Calendar className="mr-2 h-4 w-4" />
                    View Snapshot
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Results */}
          {historyData && (
            <div className="border rounded-lg p-4 bg-muted/50">
              <div className="mb-3">
                <p className="text-sm text-muted-foreground">Systems in pool at</p>
                <p className="font-medium">
                  {new Date(historyData.timestamp).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </p>
              </div>

              {historyData.systemIDs.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <p>No systems in pool at that time</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {historyData.systemIDs.length} System{historyData.systemIDs.length !== 1 ? 's' : ''}:
                    </p>
                    {renderCopyButton('snapshot', historyData.systemIDs)}
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {historyData.systemIDs.map((systemID, index) => (
                      <div
                        key={index}
                        className="font-mono text-xs bg-background rounded px-3 py-2 border"
                      >
                        {incognito.uuid(systemID)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comparison sub-panel */}
        <div className="border rounded-lg p-4 space-y-4">
          <div>
            <h3 className="text-base font-semibold">Comparison</h3>
            <p className="text-sm text-muted-foreground">
              Select two points in time to compare how the pool&apos;s composition changed between
              them.
            </p>
          </div>

          {/* State 1 / State 2 Pickers */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="state1-datetime" className="text-sm font-medium block mb-2">
                State 1 Date & Time
              </label>
              <input
                id="state1-datetime"
                type="datetime-local"
                value={fromDateTime}
                onChange={(e) => {
                  const value = e.target.value
                  if (value && toDateTime && new Date(value) >= new Date(toDateTime)) {
                    toast({
                      title: 'Invalid Range',
                      description: 'State 1 must be before State 2',
                      variant: 'destructive',
                    })
                    return
                  }
                  setFromDateTime(value)
                }}
                disabled={comparing}
                max={toDateTime || new Date().toISOString().slice(0, 16)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="state2-datetime" className="text-sm font-medium block mb-2">
                State 2 Date & Time
              </label>
              <input
                id="state2-datetime"
                type="datetime-local"
                value={toDateTime}
                onChange={(e) => {
                  const value = e.target.value
                  if (value && fromDateTime && new Date(value) <= new Date(fromDateTime)) {
                    toast({
                      title: 'Invalid Range',
                      description: 'State 2 must be after State 1',
                      variant: 'destructive',
                    })
                    return
                  }
                  setToDateTime(value)
                }}
                disabled={comparing}
                min={fromDateTime || undefined}
                max={new Date().toISOString().slice(0, 16)} // Can't pick future dates
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleCompare}
              disabled={
                comparing ||
                !fromDateTime ||
                !toDateTime ||
                new Date(toDateTime) <= new Date(fromDateTime)
              }
            >
              {comparing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <GitCompare className="mr-2 h-4 w-4" />
                  Compare States
                </>
              )}
            </Button>
          </div>

          {/* Comparison Results */}
          {comparisonData &&
            (() => {
              const state1 = comparisonData.from.systemIDs
              const state2 = comparisonData.to.systemIDs
              const state1Set = new Set(state1)
              const state2Set = new Set(state2)
              const added = state2.filter((id) => !state1Set.has(id))
              const removed = state1.filter((id) => !state2Set.has(id))

              return (
                <div className="grid grid-cols-4 gap-4">
                  {/* State 1 */}
                  <div className="border rounded-lg p-4 bg-muted/50">
                    <div className="mb-3">
                      <p className="text-sm text-muted-foreground">State 1 — systems in pool at</p>
                      <p className="font-medium">
                        {formatTimestamp(comparisonData.from.timestamp)}
                      </p>
                    </div>
                    {state1.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>No systems in pool at that time</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">
                            {state1.length} System{state1.length !== 1 ? 's' : ''}:
                          </p>
                          {renderCopyButton('state1', state1)}
                        </div>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {state1.map((systemID, index) => (
                            <div
                              key={index}
                              className="font-mono text-xs bg-background rounded px-3 py-2 border"
                            >
                              {incognito.uuid(systemID)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Added (in State 2 but not State 1) */}
                  <div className="border border-green-500/30 rounded-lg p-4 bg-green-500/10">
                    <div className="mb-3 flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-700 dark:text-green-400">
                          Added
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {added.length} System{added.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      {renderCopyButton('added', added)}
                    </div>
                    {added.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>No systems added</p>
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {added.map((systemID, index) => (
                          <div
                            key={index}
                            className="font-mono text-xs bg-background rounded px-3 py-2 border border-green-500/30"
                          >
                            {incognito.uuid(systemID)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Removed (in State 1 but not State 2) */}
                  <div className="border border-red-500/30 rounded-lg p-4 bg-red-500/10">
                    <div className="mb-3 flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-red-700 dark:text-red-400">Removed</p>
                        <p className="text-sm text-muted-foreground">
                          {removed.length} System{removed.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      {renderCopyButton('removed', removed)}
                    </div>
                    {removed.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>No systems removed</p>
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {removed.map((systemID, index) => (
                          <div
                            key={index}
                            className="font-mono text-xs bg-background rounded px-3 py-2 border border-red-500/30"
                          >
                            {incognito.uuid(systemID)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* State 2 */}
                  <div className="border rounded-lg p-4 bg-muted/50">
                    <div className="mb-3">
                      <p className="text-sm text-muted-foreground">State 2 — systems in pool at</p>
                      <p className="font-medium">{formatTimestamp(comparisonData.to.timestamp)}</p>
                    </div>
                    {state2.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>No systems in pool at that time</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">
                            {state2.length} System{state2.length !== 1 ? 's' : ''}:
                          </p>
                          {renderCopyButton('state2', state2)}
                        </div>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {state2.map((systemID, index) => (
                            <div
                              key={index}
                              className="font-mono text-xs bg-background rounded px-3 py-2 border"
                            >
                              {incognito.uuid(systemID)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}
        </div>
      </CardContent>
    </Card>
  )
}
